/**
 * Decorated hex viewer for a raw trap frame. Splits the hex string into
 * named, colour-coded segments matching the on-wire layout:
 *   12B device ID │ 1B PID │ 2B temperature ×100 │ 1B humidity │ 2B battery mV │
 *   8B timestamp ms │ 1B event count │ N × (1B type + 2B offset s) │ 2B CRC.
 */

interface Segment {
  label: string;
  hex: string;
  /** CSS variable name (without the leading --) used for both border + tinted bg. */
  color: string;
  /** Optional human-readable value shown beneath the bytes. */
  value?: string;
}

interface Props {
  rawHex: string;
  eventCount: number;
}

const HEADER_HEX = 27 * 2;
const EVENT_HEX = 3 * 2;
const CRC_HEX = 2 * 2;

function take(hex: string, offsetBytes: number, lenBytes: number): string {
  return hex.slice(offsetBytes * 2, (offsetBytes + lenBytes) * 2);
}

function readInt16BE(hex: string): number {
  const v = parseInt(hex, 16);
  return v >= 0x8000 ? v - 0x10000 : v;
}

function readUInt(hex: string): number {
  return parseInt(hex, 16);
}

function readUInt64BE(hex: string): bigint {
  return BigInt("0x" + hex);
}

function buildSegments(rawHex: string, eventCount: number): Segment[] {
  const expectedLen = HEADER_HEX + eventCount * EVENT_HEX + CRC_HEX;
  if (rawHex.length < expectedLen) {
    return [{ label: "RAW", hex: rawHex, color: "ctp-overlay0" }];
  }

  const segs: Segment[] = [];
  segs.push({
    label: "Device ID",
    hex: take(rawHex, 0, 12),
    color: "ctp-blue",
  });
  segs.push({
    label: "PID",
    hex: take(rawHex, 12, 1),
    color: "ctp-mauve",
    value: String(readUInt(take(rawHex, 12, 1))),
  });
  const tempHex = take(rawHex, 13, 2);
  segs.push({
    label: "Temp ×100",
    hex: tempHex,
    color: "ctp-peach",
    value: `${(readInt16BE(tempHex) / 100).toFixed(2)}°C`,
  });
  const humHex = take(rawHex, 15, 1);
  segs.push({
    label: "Humidity",
    hex: humHex,
    color: "ctp-sky",
    value: `${readUInt(humHex)}%`,
  });
  const battHex = take(rawHex, 16, 2);
  segs.push({
    label: "Battery mV",
    hex: battHex,
    color: "ctp-green",
    value: `${(readUInt(battHex) / 1000).toFixed(3)} V`,
  });
  const tsHex = take(rawHex, 18, 8);
  const tsMs = Number(readUInt64BE(tsHex));
  segs.push({
    label: "Timestamp",
    hex: tsHex,
    color: "ctp-yellow",
    value: new Date(tsMs).toISOString(),
  });
  const ecHex = take(rawHex, 26, 1);
  segs.push({
    label: "Event count",
    hex: ecHex,
    color: "ctp-mauve",
    value: String(readUInt(ecHex)),
  });

  for (let i = 0; i < eventCount; i++) {
    const base = 27 + i * 3;
    const typeHex = take(rawHex, base, 1);
    const offHex = take(rawHex, base + 1, 2);
    segs.push({
      label: `#${i + 1} type`,
      hex: typeHex,
      color: "ctp-red",
      value: `0x${typeHex}`,
    });
    segs.push({
      label: `#${i + 1} +s`,
      hex: offHex,
      color: "ctp-red",
      value: `+${readUInt(offHex)}s`,
    });
  }

  segs.push({
    label: "CRC",
    hex: take(rawHex, 27 + eventCount * 3, 2),
    color: "ctp-overlay0",
  });
  return segs;
}

function formatBytes(hex: string): string {
  // Group into space-separated byte pairs for readability.
  return hex.match(/.{1,2}/g)?.join(" ") ?? hex;
}

export function RawHexView({ rawHex, eventCount }: Props) {
  const segments = buildSegments(rawHex, eventCount);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 rounded border border-surface1 bg-crust p-2 font-mono text-[11px] leading-tight">
        {segments.map((s, i) => (
          <div
            key={`${s.label}-${i}`}
            className="flex flex-col items-start rounded px-1.5 py-1"
            style={{
              backgroundColor: `color-mix(in srgb, var(--${s.color}) 18%, transparent)`,
              border: `1px solid color-mix(in srgb, var(--${s.color}) 55%, transparent)`,
              color: `var(--${s.color})`,
            }}
            title={s.value ? `${s.label}: ${s.value}` : s.label}
          >
            <span className="text-[9px] uppercase tracking-wider opacity-80">
              {s.label}
            </span>
            <span className="font-mono">{formatBytes(s.hex)}</span>
            {s.value && (
              <span className="text-[9px] text-subtext0">{s.value}</span>
            )}
          </div>
        ))}
      </div>
      <details className="text-[10px] text-overlay0">
        <summary className="cursor-pointer select-none">Pełny hex (kopiuj)</summary>
        <pre className="mt-1 overflow-x-auto rounded border border-surface1 bg-crust p-2 font-mono text-[11px] text-subtext1">
          {rawHex}
        </pre>
      </details>
    </div>
  );
}
